"use strict";
/**
 * Reportan Excel grid layout shared by Reportan Web and Reportan Mobile.
 * Decides what every cell shows; rendering, gestures and editors stay per platform.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_WORK_SOURCE_OPTIONS = exports.DEFAULT_PRICE_OPTIONS = exports.DEFAULT_PAYMENT_OPTIONS = exports.DEFAULT_GRID_TIMES = exports.REPORTAN_EXCEL_MIN_DATE = exports.RUSSIAN_MONTHS = exports.HEBREW_WEEKDAYS = exports.SHEET_HEADERS = exports.ORDER_SEPARATOR_BACKGROUND = exports.REPORTAN_EXCEL_COLOR_VALUES = exports.CELL_COLOR_LABELS = exports.REPORTAN_EXCEL_COLORS = void 0;
exports.canUseReportanExcel = canUseReportanExcel;
exports.canViewReportanExcelSummary = canViewReportanExcelSummary;
exports.resolveReportanExcelScope = resolveReportanExcelScope;
exports.stripLegacyScheduleComment = stripLegacyScheduleComment;
exports.formatSinglePhoneInput = formatSinglePhoneInput;
exports.getGridSlotIndexes = getGridSlotIndexes;
exports.visitLineValues = visitLineValues;
exports.formatGridDateKey = formatGridDateKey;
exports.buildReportanExcelMonth = buildReportanExcelMonth;
exports.matchesGridSearch = matchesGridSearch;
exports.getSearchHighlightRanges = getSearchHighlightRanges;
exports.findReportanExcelSearchMatches = findReportanExcelSearchMatches;
const phone_1 = require("./phone");
function canUseReportanExcel(config, roleInfo) {
    return Boolean(config?.enabled && (roleInfo?.isAdmin || roleInfo?.isSysAdmin));
}
function canViewReportanExcelSummary(config, userId, _roleInfo) {
    return Boolean(config?.enabled && userId && config.managerUserId === userId);
}
/** A `scope` deep-link param never grants Excel by itself; it falls back to `self`. */
function resolveReportanExcelScope(requested, access) {
    if (requested === 'all')
        return 'all';
    if (requested === 'excel' && access.canOpenExcel)
        return 'excel';
    return 'self';
}
// ============ SHEET CONSTANTS ============
exports.REPORTAN_EXCEL_COLORS = [
    { value: '', color: '#ffffff', label: 'לבן — פעיל' },
    { value: 'blue', color: '#dbeafe', label: 'כחול' },
    { value: 'green', color: '#dcfce7', label: 'ירוק' },
    { value: 'yellow', color: '#fef9c3', label: 'צהוב' },
    { value: 'orange', color: '#ffedd5', label: 'כתום' },
    { value: 'red', color: '#fee2e2', label: 'אדום — בוטל' },
    { value: 'gray', color: '#f1f5f9', label: 'אפור — נדחה' },
];
exports.CELL_COLOR_LABELS = {
    '': 'לבן',
    blue: 'כחול',
    green: 'ירוק',
    yellow: 'צהוב',
    orange: 'כתום',
    red: 'אדום',
    gray: 'אפור',
};
exports.REPORTAN_EXCEL_COLOR_VALUES = Object.fromEntries(exports.REPORTAN_EXCEL_COLORS.map((item) => [item.value, item.color]));
exports.ORDER_SEPARATOR_BACKGROUND = '#ececec';
exports.SHEET_HEADERS = ['', '', 'Тел', '', 'Адрес', 'Цена', 'Комментарии'];
exports.HEBREW_WEEKDAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
exports.RUSSIAN_MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
exports.REPORTAN_EXCEL_MIN_DATE = new Date(2026, 7, 1);
exports.DEFAULT_GRID_TIMES = ['8-10', '11-13', '13-15'];
exports.DEFAULT_PAYMENT_OPTIONS = ['מזומן', 'צ׳ק', 'העברה בנקאית'];
exports.DEFAULT_PRICE_OPTIONS = [
    { value: '1750' },
    { value: '649', label: '550 + מע"מ' },
    { value: '708', label: '600 + מע"מ' },
    { value: '354', label: '300 + מע"מ' },
    { value: '1180', label: '1000 + מע"מ' },
    { value: '1298', label: '1100 + מע"מ' },
    { value: '1416', label: '1200 + מע"מ' },
    { value: '1534', label: '1300 + מע"מ' },
    { value: '1652', label: '1400 + מע"מ' },
];
exports.DEFAULT_WORK_SOURCE_OPTIONS = [
    'Игорь мт',
    'Костя мт',
    'פלס',
    'אגם',
    'פורט',
    'דור',
    'בדק בית שלנו (המלצה)',
    'בדק בית שלנו (פרסום)',
    'בדק בית שלנו (קבלנות)',
    'המלצה',
    'לקוח שלנו',
    'טרמינל',
    'אינספקט',
    'ניר - טובול דניאל יזום ובניה',
];
function compactText(value) {
    return value.replace(/[\s'"״׳]/gu, '').toLocaleLowerCase();
}
function digitsOf(value) {
    return value.replace(/\D/gu, '');
}
function isRepeatedPrice(line, price) {
    if (!price.trim())
        return false;
    const normalizedLine = compactText(line).replace(/(?:שח|₪)$/u, '');
    return normalizedLine === compactText(price);
}
function stripLegacyScheduleComment(input) {
    const time = compactText(input.time ?? '');
    const phone = digitsOf(input.phone ?? '');
    let afterFinancialHeader = false;
    return (input.comment ?? '')
        .split(/\r?\n/u)
        .map((line) => line.trim())
        .filter((line) => {
        if (!line)
            return false;
        const normalized = compactText(line);
        if (time && normalized === time)
            return false;
        if (phone && digitsOf(line) === phone)
            return false;
        if (/^In sheet phone cell looks broken\/missing; kept existing phone from prior Reportan record$/iu.test(line))
            return false;
        if (/^מקור\s+עבודה\s*:/u.test(line))
            return false;
        if (/^פיננסי$/u.test(line)) {
            afterFinancialHeader = true;
            return false;
        }
        if (afterFinancialHeader && isRepeatedPrice(line, input.price ?? '')) {
            afterFinancialHeader = false;
            return false;
        }
        afterFinancialHeader = false;
        return true;
    })
        .join('\n');
}
function formatSinglePhoneInput(value) {
    return /^[\d\s()+.\-]+$/.test(value.trim()) ? (0, phone_1.formatPhone)(value) : value;
}
function getGridSlotIndexes(visits, notes) {
    const highestSlot = Math.max(2, ...visits.map((visit) => visit.slotIndex), ...notes.map((note) => note.slotIndex));
    return Array.from({ length: highestSlot + 1 }, (_, index) => index);
}
function visitLineValues(visit, note, line, defaultTime) {
    if (!visit)
        return line === 0 ? ['', note ? '' : defaultTime, '', '', '', '', note?.text || ''] : ['', '', '', '', '', '', ''];
    // Legacy visit documents omit optional fields; cells must stay strings for search and highlighting.
    if (line === 0)
        return [visit.closer ?? '', visit.time || defaultTime, formatSinglePhoneInput(visit.phone ?? ''), '', visit.address ?? '', visit.price ?? '', stripLegacyScheduleComment(visit)];
    if (line === 1)
        return ['', '', visit.name ?? '', '', visit.email ?? '', visit.payment ?? '', visit.receipt ?? ''];
    return ['', '', '', '', '', '', note?.text || ''];
}
function formatGridDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
function groupByDate(items) {
    const grouped = new Map();
    for (const item of items) {
        const list = grouped.get(item.date);
        if (list)
            list.push(item);
        else
            grouped.set(item.date, [item]);
    }
    return grouped;
}
function buildReportanExcelMonth(input) {
    const visitsByDate = groupByDate(input.visits);
    const notesByDate = groupByDate(input.notes);
    const visitRowsByVisitId = new Map();
    for (const row of input.visitRows)
        if (!visitRowsByVisitId.has(row.visitId))
            visitRowsByVisitId.set(row.visitId, row);
    const dayCount = new Date(input.year, input.monthIndex + 1, 0).getDate();
    return Array.from({ length: dayCount }, (_, offset) => {
        const date = new Date(input.year, input.monthIndex, offset + 1);
        const key = formatGridDateKey(date);
        const dayVisits = visitsByDate.get(key) || [];
        const dayNotes = notesByDate.get(key) || [];
        const slots = getGridSlotIndexes(dayVisits, dayNotes);
        if (input.extraDates.has(key) && !slots.includes(3))
            slots.push(3);
        slots.sort((a, b) => a - b);
        const rows = [];
        for (const slotIndex of slots) {
            const hasThirdContentLine = dayNotes.some((note) => (note.slotIndex === slotIndex
                && (note.lineIndex === 2
                    || (note.lineIndex === undefined
                        && note.columnIndex === undefined
                        && dayVisits.some((visit) => visit.engineerIndex === note.engineerIndex && visit.slotIndex === slotIndex)))));
            const separatorLine = hasThirdContentLine ? 3 : 2;
            const lines = hasThirdContentLine ? [0, 1, 2, 3] : [0, 1, 2];
            for (const line of lines) {
                const isSeparator = line === separatorLine;
                rows.push({
                    key: `${key}-${slotIndex}-${line}`,
                    slotIndex,
                    line,
                    isSeparator,
                    engineers: input.engineers.map((engineer) => {
                        if (line === 3)
                            return { engineer, cells: [] };
                        const visit = dayVisits.find((item) => item.engineerIndex === engineer.index && item.slotIndex === slotIndex);
                        const rowNote = dayNotes.find((item) => (item.engineerIndex === engineer.index
                            && item.slotIndex === slotIndex
                            && item.lineIndex === undefined
                            && item.columnIndex === undefined));
                        const cellNotes = dayNotes.filter((item) => (item.engineerIndex === engineer.index
                            && item.slotIndex === slotIndex
                            && item.lineIndex !== undefined
                            && item.columnIndex !== undefined));
                        const visitRow = visit ? visitRowsByVisitId.get(visit.id) : undefined;
                        const defaultTime = exports.DEFAULT_GRID_TIMES[slotIndex] || input.defaultSlots[slotIndex] || '';
                        const values = visitLineValues(visit, rowNote, line, defaultTime);
                        return {
                            engineer,
                            cells: values.map((value, column) => {
                                const lineIndex = line;
                                const columnIndex = column;
                                const cellNote = cellNotes.find((item) => item.lineIndex === lineIndex && item.columnIndex === columnIndex);
                                const displayedValue = cellNote?.text || value;
                                const cellColor = cellNote?.color ? exports.REPORTAN_EXCEL_COLOR_VALUES[cellNote.color] : undefined;
                                const background = cellColor
                                    || (isSeparator ? exports.ORDER_SEPARATOR_BACKGROUND : exports.REPORTAN_EXCEL_COLOR_VALUES[rowNote?.color || visitRow?.color || ''])
                                    || '#fff';
                                return {
                                    id: `${key}-${engineer.index}-${slotIndex}-${line}-${column}`,
                                    value: displayedValue,
                                    background,
                                    target: {
                                        date: key,
                                        engineer,
                                        slotIndex,
                                        lineIndex,
                                        columnIndex,
                                        defaultTime,
                                        displayedValue,
                                        visit,
                                        rowNote,
                                        cellNote,
                                        color: visitRow?.color,
                                    },
                                };
                            }),
                        };
                    }),
                });
            }
        }
        return {
            key,
            date,
            dayOfMonth: date.getDate(),
            weekday: exports.HEBREW_WEEKDAYS[date.getDay()],
            canAddFourthSlot: !slots.includes(3),
            rows,
        };
    });
}
// ============ SEARCH ============
function matchesGridSearch(value, query) {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery)
        return false;
    return value.toLocaleLowerCase().includes(normalizedQuery) || (0, phone_1.phoneMatch)(value, query);
}
function getSearchHighlightRanges(value, query) {
    const normalizedValue = value.toLocaleLowerCase();
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery)
        return [];
    const directRanges = [];
    let directIndex = normalizedValue.indexOf(normalizedQuery);
    while (directIndex >= 0) {
        directRanges.push({ start: directIndex, end: directIndex + normalizedQuery.length });
        directIndex = normalizedValue.indexOf(normalizedQuery, directIndex + normalizedQuery.length);
    }
    if (directRanges.length > 0)
        return directRanges;
    if (!/^[\d\s()+.\-]+$/.test(normalizedQuery))
        return [];
    const queryDigits = normalizedQuery.replace(/\D/g, '');
    if (queryDigits.length < 3)
        return [];
    const digitIndexes = Array.from(value, (character, index) => (/\d/.test(character) ? index : -1))
        .filter((index) => index >= 0);
    const valueDigits = digitIndexes.map((index) => value[index]).join('');
    const digitStart = valueDigits.indexOf(queryDigits);
    if (digitStart < 0)
        return [];
    return [{ start: digitIndexes[digitStart], end: digitIndexes[digitStart + queryDigits.length - 1] + 1 }];
}
/** Cell ids in sheet order: day, slot, line, engineer, column. */
function findReportanExcelSearchMatches(days, query) {
    if (!query.trim())
        return [];
    const matches = [];
    for (const day of days) {
        for (const row of day.rows) {
            for (const group of row.engineers) {
                for (const cell of group.cells) {
                    if (matchesGridSearch(cell.value, query))
                        matches.push(cell.id);
                }
            }
        }
    }
    return matches;
}
